export const NOTIFICATION_FILTERS = [
  { value: "all", label: "Todos los tipos" },
  { value: "mails", label: "Mails", codes: ["mail_received"] },
  {
    value: "chain_finished",
    label: "Comunicación terminada",
    codes: ["mail_chain_finished"],
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    codes: ["whatsapp_received", "whatsapp_template_status"],
    requiresWhatsApp: true,
  },
  { value: "leads", label: "Leads", codes: ["lead_status_changed"] },
  { value: "reminders", label: "Recordatorios", codes: ["follow_up_reminder"] },
  {
    value: "validation",
    label: "Prospección",
    codes: [
      "bulk_search_finished",
      "bulk_verification_finished",
      "single_verification_finished",
    ],
  },
  { value: "database", label: "Database", codes: ["person_import_finished"] },
];

const SEVERITIES = ["info", "success", "warning", "error"];

const SEVERITY_LABELS = {
  warning: "Atención",
  error: "Error",
};

const DAY_FORMATTER = new Intl.DateTimeFormat("es-AR", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

const TIME_FORMATTER = new Intl.DateTimeFormat("es-AR", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const MILLISECONDS_PER_DAY = 86400000;

export function getAvailableFilters({ whatsappEnabled = false } = {}) {
  return NOTIFICATION_FILTERS.filter(
    (filter) => !filter.requiresWhatsApp || whatsappEnabled,
  );
}

export function getFilterTypeCodes(filterValue) {
  const filter = NOTIFICATION_FILTERS.find(
    (option) => option.value === filterValue,
  );
  return filter?.codes ?? null;
}

export function getTypeCode(notification) {
  return String(notification?.notification_type?.code || "");
}

export function matchesFilter(notification, filterValue) {
  const codes = getFilterTypeCodes(filterValue);
  if (!codes) return true;
  return codes.includes(getTypeCode(notification));
}

export function getSeverity(notification) {
  const severity = String(notification?.severity || "").toLowerCase();
  return SEVERITIES.includes(severity) ? severity : "info";
}

export function getSeverityLabel(notification) {
  return SEVERITY_LABELS[getSeverity(notification)] || "";
}

export function getContextLabel(notification) {
  const payload = notification?.payload || {};

  switch (getTypeCode(notification)) {
    case "mail_received":
    case "whatsapp_received":
      return payload.lead_name || payload.from_email || "";
    case "lead_status_changed":
      return [payload.lead_name, payload.new_status?.name]
        .filter(Boolean)
        .join(" · ");
    case "mail_chain_finished":
      return [payload.lead_name, payload.template_group_name]
        .filter(Boolean)
        .join(" · ");
    case "follow_up_reminder":
      return payload.lead_name || "";
    default:
      return "";
  }
}

function parseDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function daysBetween(date, now) {
  return Math.round((startOfDay(now) - startOfDay(date)) / MILLISECONDS_PER_DAY);
}

export function getDayKey(dateString) {
  const date = parseDate(dateString);
  if (!date) return "sin-fecha";
  const dayStart = startOfDay(date);
  const month = String(dayStart.getMonth() + 1).padStart(2, "0");
  const day = String(dayStart.getDate()).padStart(2, "0");
  return `${dayStart.getFullYear()}-${month}-${day}`;
}

export function getDayLabel(dateString, now = new Date()) {
  const date = parseDate(dateString);
  if (!date) return "Sin fecha";
  const daysAgo = daysBetween(date, now);
  if (daysAgo <= 0) return "Hoy";
  if (daysAgo === 1) return "Ayer";
  return DAY_FORMATTER.format(date);
}

export function formatRelativeTime(dateString, now = new Date()) {
  const date = parseDate(dateString);
  if (!date) return "";

  const minutes = Math.floor((now - date) / 1000 / 60);
  const hours = Math.floor(minutes / 60);
  const daysAgo = daysBetween(date, now);

  if (minutes < 1) return "Hace un momento";
  if (minutes < 60) return `Hace ${minutes} min`;
  if (daysAgo <= 0) return `Hace ${hours} hora${hours > 1 ? "s" : ""}`;
  if (daysAgo === 1) return `Ayer, ${TIME_FORMATTER.format(date)}`;
  return DAY_FORMATTER.format(date);
}

export function groupByDay(notifications, now = new Date()) {
  const groups = [];
  const groupsByKey = new Map();

  notifications.forEach((notification) => {
    const key = getDayKey(notification?.created_at);

    if (!groupsByKey.has(key)) {
      const group = {
        key,
        label: getDayLabel(notification?.created_at, now),
        items: [],
        unreadCount: 0,
      };
      groupsByKey.set(key, group);
      groups.push(group);
    }

    const group = groupsByKey.get(key);
    group.items.push(notification);
    if (!notification?.is_read) group.unreadCount += 1;
  });

  return groups;
}

function normalizeText(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

export function matchesSearch(notification, search) {
  const tokens = normalizeText(search).split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;

  const payload = notification?.payload || {};
  const haystack = normalizeText(
    [
      notification?.title,
      notification?.message,
      notification?.account_name,
      notification?.notification_type?.name,
      payload.lead_name,
      payload.from_email,
      payload.subject,
    ]
      .filter(Boolean)
      .join(" "),
  );

  return tokens.every((token) => haystack.includes(token));
}
